import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApi, useRouteRefParams } from '@backstage/frontend-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import {
  getRuleSeverityFromResources,
  pluralize,
  RULE_SEVERITIES,
  RuleEntity,
  RuleSetEntity,
  scanLabel,
} from '@internal/backstage-plugin-catalog-devops-common';
import { ShortInfoRuleDrawer } from '../Drawers';
import { useRulesContext } from '../Providers';
import { rulesetsOf } from '../EntityPages/shared';
import {
  PageSkeleton,
  useSelectPopover,
} from '@internal/backstage-plugin-devops-react';
import { ComplianceFrameworksDialog, FrameworkInfo } from './ComplianceFrameworksDialog';
import styles from '../compliance-page.module.css';

const CATEGORIES = ['Security', 'Finance', 'Housekeeping'];

/** Category accent class (dot + bar), from the mockup. */
const accentOf = (category: string): string => {
  if (category === 'Security') {
    return styles.accentSecurity;
  }
  if (category === 'Finance') {
    return styles.accentFinance;
  }
  return styles.accentHousekeeping;
};

const RING_CIRCUMFERENCE = 257.6;

/** Sort comparator shared by the table columns. */
const compareValues = (a: string | number, b: string | number): number => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

type CategoryFilter = 'all' | (typeof CATEGORIES)[number];
type StatusFilter = 'Failed' | 'Passed' | 'Unknown' | 'All';
type SortKey = '' | 'name' | 'severity' | 'status' | 'affected' | 'saving';

const severityRank = (severity: string): number => {
  const index = (RULE_SEVERITIES as readonly string[]).indexOf(severity);
  return index === -1 ? RULE_SEVERITIES.length : index;
};

const ruleTitle = (rule: RuleEntity) =>
  rule.metadata.title ?? rule.metadata.name;

/** Worst severity across the rule's affected resources. */
const severityOf = (rule: RuleEntity): string =>
  getRuleSeverityFromResources(
    rule.spec.resources as Array<{ severity?: string }>,
  ) ?? 'Unknown';

const affectedCount = (rule: RuleEntity) => rule.spec.resources?.length ?? 0;

/** Max saving of a rule, or null when the export has no usable numbers. */
const maxSavingOf = (rule: RuleEntity): number | null => {
  const max = Number(rule.spec.maxCostSaving);
  return Number.isFinite(max) && max > 0 ? max : null;
};

const savingText = (rule: RuleEntity): string => {
  const max = maxSavingOf(rule);
  if (max === null) {
    return '';
  }
  return `$${max.toLocaleString('en-US')}/mo`;
};

const pillClass = (classes: Record<string, string>, value: string): string => {
  const normalized = value.toLowerCase();
  const known = [
    'critical',
    'high',
    'medium',
    'low',
    'info',
    'unknown',
    'failed',
    'passed',
  ];
  return known.includes(normalized)
    ? classes[`pill${normalized[0].toUpperCase()}${normalized.slice(1)}`]
    : classes.pillUnknown;
};

/**
 * Compliance tab, 1:1 with design/compliance-page-mockup.html: compliance
 * score + clickable category cards, stat strip, frameworks strip (RuleSets,
 * benchmarks § and controls ◆), toolbar with removable filter chips and a
 * grouped rules table. Deep-links: ?cat=Security&st=Failed.
 */
export function ComplianceTab() {
  const { name: accountName } = useRouteRefParams(entityRouteRef);
  const { search } = useLocation();
  const { rules, severities, selectRule, isLoading } = useRulesContext();
  const severitySelect = useSelectPopover();
  const catalogApi = useApi(catalogApiRef);

  // Deep links (?cat=&st=) are read once on load, like the resources tab.
  const [initialCat, initialStatus] = useMemo(() => {
    const params = new URLSearchParams(search);
    const cat = params.get('cat');
    const st = params.get('st');
    return [
      CATEGORIES.includes(cat as CategoryFilter) ? (cat as CategoryFilter) : 'all',
      ['Failed', 'Passed', 'Unknown', 'All'].includes(st ?? '')
        ? (st as StatusFilter)
        : 'Failed',
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [category, setCategory] = useState<CategoryFilter>(initialCat);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [selectedSeverities, setSelectedSeverities] = useState<Set<string>>(
    new Set(),
  );
  const [selectedFrameworks, setSelectedFrameworks] = useState<Set<string>>(
    new Set(),
  );
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('');
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [ruleSets, setRuleSets] = useState<RuleSetEntity[]>([]);
  const [fwDialogOpen, setFwDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'RuleSet', 'spec.account': accountName },
      })
      .then(({ items }) => {
        if (!cancelled) {
          setRuleSets(items as RuleSetEntity[]);
        }
      })
      .catch(() => {
        // The page stays usable without the frameworks strip.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accountName]);

  const rulesByRuleset = useMemo(() => {
    const map = new Map<string, RuleEntity[]>();
    rules.forEach(rule => {
      rulesetsOf(rule).forEach(rulesetName => {
        map.set(rulesetName, [...(map.get(rulesetName) ?? []), rule]);
      });
    });
    return map;
  }, [rules]);

  const frameworks = useMemo<FrameworkInfo[]>(() => {
    return ruleSets.map(ruleSet => {
      const mapped = rulesByRuleset.get(ruleSet.metadata.name) ?? [];
      const failed = mapped.filter(rule => rule.spec.result === 'Failed').length;
      const passed = mapped.filter(rule => rule.spec.result === 'Passed').length;
      const rawType = (
        ruleSet.spec.rulesetType ??
        ruleSet.spec.type ??
        'benchmark'
      ).toLowerCase();
      return {
        name: ruleSet.metadata.name,
        title: ruleSet.metadata.title ?? ruleSet.metadata.name,
        type: rawType === 'controls' ? 'controls' : 'benchmark',
        rulesTotal: mapped.length,
        failed,
        pctPassed: mapped.length ? Math.round((passed / mapped.length) * 100) : null,
      };
    });
  }, [ruleSets, rulesByRuleset]);

  const frameworkByTitle = useMemo(() => {
    const map = new Map<string, string>();
    frameworks.forEach(fw => map.set(fw.name, fw.title));
    return map;
  }, [frameworks]);

  // The strip shows coverage, so only frameworks the account's rules
  // actually map to. The scanner also emits its whole ruleset registry
  // (foreign-cloud benchmarks with zero rules land here - e.g. Azure
  // benchmarks on an AWS account); those stay available in the
  // "All frameworks" dialog, which keeps the full registry.
  const rankedFrameworks = useMemo(
    () =>
      frameworks
        .filter(fw => fw.rulesTotal > 0)
        .sort((a, b) => b.failed - a.failed),
    [frameworks],
  );

  const inFrameworkScope = (rule: RuleEntity) =>
    selectedFrameworks.size === 0 ||
    rulesetsOf(rule).some(ruleset => selectedFrameworks.has(ruleset));

  const scopedRules = useMemo(
    () => rules.filter(inFrameworkScope),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules, selectedFrameworks],
  );

  const failedByCategory = (cat: string) =>
    scopedRules.filter(rule => rule.spec.category === cat && rule.spec.result === 'Failed')
      .length;

  // Stat strip + score, scoped by frameworks and category (mockup semantics:
  // Unknown rules are excluded from the score, stats count Failed only).
  const stats = useMemo(() => {
    const rows =
      category === 'all'
        ? scopedRules
        : scopedRules.filter(rule => rule.spec.category === category);
    const failed = rows.filter(rule => rule.spec.result === 'Failed');
    const scored = rows.filter(rule => rule.spec.result !== 'Unknown');
    const passedInScored = scored.filter(rule => rule.spec.result === 'Passed');
    const score = scored.length
      ? Math.round((passedInScored.length / scored.length) * 100)
      : 0;
    const affected = failed.reduce(
      (sum, rule) => sum + affectedCount(rule),
      0,
    );
    const regions = new Set(
      failed.flatMap(rule =>
        (rule.spec.resources ?? [])
          .map(resource => resource.id.split('_')[1])
          .filter((region): region is string => Boolean(region)),
      ),
    );
    const saving = failed.reduce(
      (sum, rule) => sum + (maxSavingOf(rule) ?? 0),
      0,
    );
    return {
      rowsLength: rows.length,
      unknown: rows.length - scored.length,
      failed,
      failedCount: failed.length,
      score,
      affected,
      regions: regions.size,
      saving,
    };
  }, [category, scopedRules]);

  const filteredRules = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = scopedRules.filter(rule => {
      if (category !== 'all' && rule.spec.category !== category) {
        return false;
      }
      if (status !== 'All' && rule.spec.result !== status) {
        return false;
      }
      if (
        selectedSeverities.size > 0 &&
        !selectedSeverities.has(severityOf(rule) ?? 'Unknown')
      ) {
        return false;
      }
      if (q) {
        const haystack = [
          ruleTitle(rule),
          rule.metadata.description ?? '',
          rulesetsOf(rule).map(n => frameworkByTitle.get(n) ?? n).join(' '),
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });

    if (!sortKey) {
      return rows;
    }
    const value = (rule: RuleEntity): string | number => {
      switch (sortKey) {
        case 'name':
          return ruleTitle(rule);
        case 'severity':
          return severityRank(severityOf(rule) ?? 'Unknown');
        case 'status':
          return rule.spec.result ?? '';
        case 'affected':
          return affectedCount(rule);
        case 'saving':
          return maxSavingOf(rule) ?? 0;
        default:
          return '';
      }
    };
    return [...rows].sort((a, b) =>
      compareValues(value(a), value(b)) * sortDir,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scopedRules,
    category,
    status,
    selectedSeverities,
    frameworkByTitle,
    query,
    sortKey,
    sortDir,
  ]);

  const toggleSeverity = (severity: string) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev);
      if (next.has(severity)) {
        next.delete(severity);
      } else {
        next.add(severity);
      }
      return next;
    });
  };

  const toggleFramework = (rulesetName: string) => {
    setSelectedFrameworks(prev => {
      const next = new Set(prev);
      if (next.has(rulesetName)) {
        next.delete(rulesetName);
      } else {
        next.add(rulesetName);
      }
      return next;
    });
  };

  const resetFilters = () => {
    setCategory('all');
    setStatus('Failed');
    setSelectedSeverities(new Set());
    setSelectedFrameworks(new Set());
    setQuery('');
    setSortKey('');
  };

  const sortArrow = (key: SortKey) => {
    if (sortKey !== key) {
      return '↕';
    }
    return sortDir === 1 ? '↑' : '↓';
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir(1);
    } else if (sortDir === 1) {
      setSortDir(-1);
    } else {
      setSortKey('');
    }
  };

  const exportCsv = () => {
    const header = 'Rule,Code,Category,Severity,Status,Affected,Max saving,Frameworks,Last scan';
    const lines = filteredRules.map(rule =>
      [
        ruleTitle(rule),
        rule.spec.type ?? '',
        rule.spec.category ?? '',
        severityOf(rule) ?? 'Unknown',
        rule.spec.result ?? '',
        affectedCount(rule),
        maxSavingOf(rule) ?? 0,
        rulesetsOf(rule).map(n => frameworkByTitle.get(n) ?? n).join(' | '),
        rule.spec.reportCreationTime ?? '',
      ]
        .map(cell => `"${String(cell).replace(/"/g, '""')}"`)
        .join(','),
    );
    const blob = new Blob([[header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `compliance-${accountName}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const frameworksCell = (rule: RuleEntity) => {
    const tags = rulesetsOf(rule);
    if (tags.length === 0) {
      return <span className={styles.muted}>—</span>;
    }
    return (
      <>
        {tags.slice(0, 2).map(rulesetName => {
          const isControls =
            frameworks.find(fw => fw.name === rulesetName)?.type === 'controls';
          return (
            <span
              key={rulesetName}
              className={`${styles.fwTag} ${isControls ? styles.fwTagControl : styles.fwTagBench}`}
              title={frameworkByTitle.get(rulesetName) ?? rulesetName}
            >
              {frameworkByTitle.get(rulesetName) ?? rulesetName}
            </span>
          );
        })}
        {tags.length > 2 && (
          <span className={styles.muted}>+{tags.length - 2}</span>
        )}
      </>
    );
  };

  const ruleRow = (rule: RuleEntity) => {
    const saving = savingText(rule);
    return (
      <tr
        key={rule.metadata.name}
        className={styles.row}
        onClick={() => selectRule(rule)}
      >
        <td>
          <Link
            to={`/catalog/default/rule/${rule.metadata.name}`}
            className={styles.ruleName}
            target="_blank"
            rel="noopener noreferrer"
            onClick={event => event.stopPropagation()}
          >
            {ruleTitle(rule)}
          </Link>
          <div className={styles.ruleCode}>{rule.spec.type}</div>
        </td>
        <td>
          <div className={styles.ruleDesc} title={rule.metadata.description}>
            {rule.metadata.description ?? ''}
          </div>
        </td>
        <td>
          <span className={`${styles.pill} ${pillClass(styles, severityOf(rule) ?? 'Unknown')}`}>
            <span className={styles.pillDot} />
            {severityOf(rule) ?? 'Unknown'}
          </span>
        </td>
        <td>
          <span className={`${styles.pill} ${pillClass(styles, rule.spec.result ?? 'Unknown')}`}>
            <span className={styles.pillDot} />
            {rule.spec.result}
          </span>
        </td>
        <td className={styles.num}>{affectedCount(rule) || '—'}</td>
        <td>
          {saving ? (
            <span className={styles.saving}>{saving}</span>
          ) : (
            <span className={styles.muted}>—</span>
          )}
        </td>
        <td>{frameworksCell(rule)}</td>
        <td className={styles.muted} style={{ whiteSpace: 'nowrap' }}>
          {scanLabel(rule.spec.reportCreationTime)}
        </td>
      </tr>
    );
  };

  const grouped = category === 'all' && !sortKey;
  const tableRows = grouped
    ? CATEGORIES.map(cat => {
        const sub = filteredRules.filter(rule => rule.spec.category === cat);
        if (sub.length === 0) {
          return null;
        }
        const failed = sub.filter(rule => rule.spec.result === 'Failed').length;
        return (
          <Fragment key={`group-${cat}`}>
            <tr className={styles.groupRow}>
              <td colSpan={8}>
                {cat}
                <span className={styles.groupCount}>
                  {pluralize(sub.length, 'rule')} · {failed} failed
                </span>
              </td>
            </tr>
            {sub.map(ruleRow)}
          </Fragment>
        );
      })
    : filteredRules.map(ruleRow);

  const chips: { key: string; label: string; tip: string; onRemove: () => void }[] =
    [];
  if (category !== 'all') {
    chips.push({
      key: 'cat',
      label: category,
      tip: category,
      onRemove: () => setCategory('all'),
    });
  }
  if (status !== 'Failed') {
    chips.push({
      key: 'st',
      label: status,
      tip: `Status: ${status}`,
      onRemove: () => setStatus('Failed'),
    });
  }
  selectedSeverities.forEach(severity => {
    chips.push({
      key: `sev:${severity}`,
      label: severity,
      tip: `Severity: ${severity}`,
      onRemove: () => toggleSeverity(severity),
    });
  });
  selectedFrameworks.forEach(rulesetName => {
    chips.push({
      key: `fw:${rulesetName}`,
      label: frameworkByTitle.get(rulesetName) ?? rulesetName,
      tip: `Framework: ${frameworkByTitle.get(rulesetName) ?? rulesetName}`,
      onRemove: () => toggleFramework(rulesetName),
    });
  });
  if (query) {
    chips.push({
      key: 'q',
      label: `“${query}”`,
      tip: 'Search',
      onRemove: () => setQuery(''),
    });
  }

  const emptyTitle =
    selectedFrameworks.size > 0 && scopedRules.length === 0
      ? 'No rules are mapped to the selected framework(s) yet'
      : 'No rules match the current filters';

  const ringClass = () => {
    if (stats.score >= 80) {
      return styles.ringSuccess;
    }
    if (stats.score >= 50) {
      return styles.ringWarning;
    }
    return styles.ringDanger;
  };

  if (isLoading) {
    return <PageSkeleton rows={10} />;
  }

  return (
    <>
      <ShortInfoRuleDrawer />

      {/* summary band: score + category cards */}
      <div className={styles.summary}>
        <div className={`${styles.card} ${styles.scoreCard}`}>
          <div className={styles.ring}>
            <svg width="96" height="96" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="41"
                fill="none"
                stroke="var(--bui-bg-neutral-2)"
                strokeWidth="9"
              />
              <circle
                cx="48"
                cy="48"
                r="41"
                fill="none"
                className={ringClass()}
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - stats.score / 100)}
              />
            </svg>
            <div className={styles.ringVal}>
              <div>
                <b>{stats.score}</b>
                <span>score</span>
              </div>
            </div>
          </div>
          <div className={styles.scoreMeta}>
            <h3>Compliance score</h3>
            <p>
              Passed ÷ (passed + failed) across all categories;{' '}
              <b>Unknown</b> rules are excluded.
            </p>
          </div>
        </div>

        <div className={styles.catGrid}>
          {CATEGORIES.map(cat => {
            const rows = scopedRules.filter(rule => rule.spec.category === cat);
            const passed = rows.filter(rule => rule.spec.result === 'Passed').length;
            const failed = rows.length - passed;
            const pct = rows.length ? Math.round((passed / rows.length) * 100) : 0;
            const affected = rows.reduce(
              (sum, rule) => sum + affectedCount(rule),
              0,
            );
            const top = rows
              .filter(rule => rule.spec.result === 'Failed')
              .sort((a, b) => affectedCount(b) - affectedCount(a))[0];
            const accentClass = accentOf(cat);
            return (
              <button
                key={cat}
                type="button"
                className={`${styles.card} ${styles.catCard} ${category === cat ? styles.catCardSelected : ''}`}
                onClick={() =>
                  setCategory(category === cat ? 'all' : (cat as CategoryFilter))
                }
              >
                <div className={styles.catHead}>
                  <span className={styles.catName}>
                    <span className={`${styles.catNameDot} ${accentClass}`} />
                    {cat}
                  </span>
                  <span className={styles.fwMeta}>{pluralize(rows.length, 'rule')}</span>
                </div>
                <div className={styles.catCounts}>
                  <b>
                    {passed}
                    <span>/{rows.length} passed</span>
                  </b>
                </div>
                <div className={styles.bar}>
                  <i className={accentClass} style={{ width: `${pct}%` }} />
                </div>
                <div className={styles.catFoot}>
                  <span>
                    {failed} failed · {affected} affected
                  </span>
                  <span>
                    {top
                      ? `top: ${ruleTitle(top).split(' ').slice(0, 3).join(' ')}…`
                      : 'all clear ✓'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* stat strip */}
      <div className={styles.statStrip}>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Failed rules</span>
          <span className={styles.v}>
            {stats.failedCount}
            <small>
              of {stats.rowsLength}
              {stats.unknown > 0 ? ` · ${stats.unknown} unknown` : ''}
            </small>
          </span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Affected resources</span>
          <span className={`${styles.v} ${styles.num}`}>{stats.affected}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Affected regions</span>
          <span className={`${styles.v} ${styles.num}`}>{stats.regions}</span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.k}>Est. monthly saving</span>
          <span className={`${styles.v} ${styles.vMoney}`}>
            {stats.saving
              ? `$${Math.round(stats.saving).toLocaleString('en-US')}`
              : '$0'}
          </span>
        </div>
      </div>

      {/* frameworks strip: top-4 worst + "+N more" */}
      <div className={styles.fwBand}>
        <div className={styles.fwScroll}>
          {rankedFrameworks.slice(0, 4).map(fw => (
            <button
              key={fw.name}
              type="button"
              className={`${styles.card} ${styles.fwCard} ${selectedFrameworks.has(fw.name) ? styles.fwCardSelected : ''}`}
              onClick={() => toggleFramework(fw.name)}
              title={fw.title}
            >
              <div className={styles.fwHead}>
                <span
                  className={`${styles.fwType} ${fw.type === 'benchmark' ? styles.fwTypeBenchmark : styles.fwTypeControls}`}
                >
                  {fw.type}
                </span>
                <span className={styles.fwMeta}>{pluralize(fw.rulesTotal, 'rule')}</span>
              </div>
              <div className={styles.fwName}>{fw.title}</div>
              {fw.pctPassed === null ? (
                <span className={styles.fwMeta}>
                  no rules mapped — automation gap
                </span>
              ) : (
                <>
                  <div className={styles.fwStats}>
                    <span className={styles.fwPct}>{fw.pctPassed}%</span>
                    <span className={styles.fwMeta}>
                      passed · {fw.failed} failed
                    </span>
                  </div>
                  <div className={styles.bar}>
                    <i
                      className={
                        fw.type === 'benchmark'
                          ? styles.fwBarBenchmark
                          : styles.fwBarControls
                      }
                      style={{ width: `${fw.pctPassed}%` }}
                    />
                  </div>
                </>
              )}
            </button>
          ))}
          {rankedFrameworks.length > 4 && (
            <button
              type="button"
              className={`${styles.card} ${styles.fwCard} ${styles.fwMore}`}
              onClick={() => setFwDialogOpen(true)}
              title="All frameworks and controls"
            >
              <div className={styles.fwHead}>
                <span className={`${styles.fwType} ${styles.fwTypeAll}`}>
                  +{rankedFrameworks.length - 4} more
                </span>
              </div>
              <div className={styles.fwName}>All frameworks…</div>
              <span className={styles.fwMeta}>browse, search &amp; select</span>
            </button>
          )}
        </div>
      </div>

      {/* toolbar */}
      <div className={styles.toolbar} role="toolbar" aria-label="Compliance filters">
        <ToggleButtonGroup
          aria-label="Category"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([category])}
          onSelectionChange={keys => {
            const next = [...keys][0] as CategoryFilter | undefined;
            setCategory(next ?? 'all');
          }}
        >
          <ToggleButton key="all" id="all" size="small">
            All
          </ToggleButton>
          {CATEGORIES.map(cat => (
            <ToggleButton key={cat} id={cat} size="small">
              {cat}{' '}
              <span className={styles.segCount}>{failedByCategory(cat)}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <label className={styles.search}>
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search rules, resource types, tags…"
            aria-label="Search rules"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </label>

        <button
          type="button"
          className={styles.selectBtn}
          aria-haspopup="dialog"
          onClick={() => setFwDialogOpen(true)}
        >
          Framework
          {selectedFrameworks.size > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedFrameworks.size}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>

        <button
          type="button"
          ref={severitySelect.triggerRef}
          className={styles.selectBtn}
          onClick={severitySelect.toggle}
          aria-expanded={severitySelect.isOpen}
          aria-haspopup="true"
        >
          Severity
          {selectedSeverities.size > 0 && (
            <span className={styles.selectBtnCount}>
              {selectedSeverities.size}
            </span>
          )}{' '}
          <span className={styles.selectBtnArrow}>▼</span>
        </button>
        <Popover {...severitySelect.popoverProps} placement="bottom start" hideArrow>
            <div className={styles.sevPopover}>
              {severities.map(severity => (
                <label key={severity} className={styles.fwRow}>
                  <input
                    type="checkbox"
                    checked={selectedSeverities.has(severity)}
                    onChange={() => toggleSeverity(severity)}
                  />
                  <span
                    className={`${styles.pill} ${pillClass(styles, severity)}`}
                  >
                    <span className={styles.pillDot} />
                    {severity}
                  </span>
                </label>
              ))}
            </div>
        </Popover>

        <ToggleButtonGroup
          aria-label="Status"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([status])}
          onSelectionChange={keys => {
            const next = [...keys][0] as StatusFilter | undefined;
            setStatus(next ?? 'Failed');
          }}
        >
          {(['Failed', 'Passed', 'Unknown', 'All'] as StatusFilter[]).map(st => (
            <ToggleButton key={st} id={st} size="small">
              {st}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <button
          type="button"
          className={styles.exportBtn}
          style={{ marginLeft: 'auto' }}
          onClick={exportCsv}
          title="Export filtered rules to CSV"
        >
          ↓ Export
        </button>
      </div>

      {/* removable filter chips */}
      <div className={styles.chips}>
        {chips.map(chip => (
          <span key={chip.key} className={styles.chip} title={chip.tip}>
            <b>{chip.label}</b>
            <button
              type="button"
              className={styles.chipButton}
              onClick={chip.onRemove}
              aria-label="Remove filter"
            >
              ✕
            </button>
          </span>
        ))}
        {chips.length > 0 && (
          <button type="button" className={styles.chipClear} onClick={resetFilters}>
            Clear all
          </button>
        )}
        <span className={styles.resultNote}>
          {filteredRules.length} of {pluralize(rules.length, 'rule')} shown
        </span>
      </div>

      {/* rules table */}
      <div className={`${styles.card} ${styles.tableCard}`}>
        <table className={styles.table} aria-label="Compliance rules">
          <thead>
            <tr>
              <th
                className={`${styles.sortable} ${sortKey === 'name' ? styles.sorted : ''}`}
                onClick={() => toggleSort('name')}
              >
                Rule <span className={styles.sArrow}>{sortArrow('name')}</span>
              </th>
              <th>Description</th>
              <th
                className={`${styles.sortable} ${sortKey === 'severity' ? styles.sorted : ''}`}
                onClick={() => toggleSort('severity')}
              >
                Severity{' '}
                <span className={styles.sArrow}>{sortArrow('severity')}</span>
              </th>
              <th
                className={`${styles.sortable} ${sortKey === 'status' ? styles.sorted : ''}`}
                onClick={() => toggleSort('status')}
              >
                Status <span className={styles.sArrow}>{sortArrow('status')}</span>
              </th>
              <th
                className={`${styles.sortable} ${sortKey === 'affected' ? styles.sorted : ''}`}
                onClick={() => toggleSort('affected')}
              >
                Affected{' '}
                <span className={styles.sArrow}>{sortArrow('affected')}</span>
              </th>
              <th
                className={`${styles.sortable} ${sortKey === 'saving' ? styles.sorted : ''}`}
                onClick={() => toggleSort('saving')}
              >
                Est. saving{' '}
                <span className={styles.sArrow}>{sortArrow('saving')}</span>
              </th>
              <th>Frameworks</th>
              <th>Last scan</th>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
        {filteredRules.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyBig}>✓</div>
            <b>{emptyTitle}</b>
            Everything is passing, or filters are too narrow.
            <br />
            <button type="button" className={styles.emptyButton} onClick={resetFilters}>
              Clear all filters
            </button>
          </div>
        )}
      </div>

      <ComplianceFrameworksDialog
        open={fwDialogOpen}
        onClose={() => setFwDialogOpen(false)}
        frameworks={frameworks}
        selected={selectedFrameworks}
        onToggle={toggleFramework}
        onClear={() => setSelectedFrameworks(new Set())}
      />
    </>
  );
}
